import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError, formatFileSize } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PenTool, Plus, Send, RefreshCw, Download, FileSignature, ExternalLink, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { uploadWithProgress } from "@/lib/downloadWithProgress";
import DocumentTitleSelect from "@/components/DocumentTitleSelect";
import ClientCombobox from "@/components/ClientCombobox";
import StaffEmailCombobox from "@/components/StaffEmailCombobox";
import SignaturePreviewDialog, { isPreviewSkipped } from "@/components/SignaturePreviewDialog";
import SignatureDocTypesEditor from "@/components/SignatureDocTypesEditor";
import SignaturePlacementCanvas from "@/components/SignaturePlacementCanvas";
import { DropZone } from "@/components/DropZone";
import { UploadCloud, FileText, X as XIcon, CheckCircle2, Settings, MousePointerClick } from "lucide-react";

const StatusBadge = ({ status }) => {
  if (status === "signed") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">✓ Firmato</Badge>;
  if (status === "sent") return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">📨 Inviato</Badge>;
  return <Badge variant="outline">📝 Bozza</Badge>;
};

/**
 * 🆕 Feb 2026 — Centro Firme
 * Gestione firme libere (documenti PDF ad hoc, non legati a polizze/preventivi).
 * Le firme legate a polizze/preventivi/allegati appaiono nella pagina "Firme OTP".
 */
export default function SignatureCenter() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [clients, setClients] = useState([]);
  const [previewDialog, setPreviewDialog] = useState(null); // {fh, data, mode: 'send'|'resend'}
  const [previewBusy, setPreviewBusy] = useState(false);
  // 🆕 Feb 2026 — Segnaposto firma opt-in (normalizzati 0..1: {page, x, y, w, h})
  const [placements, setPlacements] = useState([]);
  const [openPlacementUi, setOpenPlacementUi] = useState(false);
  // 🆕 Feb 2026 — Gestione tipi documento firma (modal + versionamento per refresh dropdown)
  const [openTitles, setOpenTitles] = useState(false);
  const [titlesVersion, setTitlesVersion] = useState(0);
  // 🆕 Feb 2026 — Selezione multipla + eliminazione OTP
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState(null); // { ids: string[], stage: 'confirm'|'otp' }
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);
  // 🆕 Feb 2026 — Vista cestino (soft-deleted)
  const [showTrash, setShowTrash] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const fileInputRef = useRef(null);
  const [requireDrawn, setRequireDrawn] = useState(false);
  const [newForm, setNewForm] = useState({
    title: "",
    recipient_name: "",
    recipient_email: "",
    recipient_mobile: "",
    notify_email: "",
    file: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/freehand-documents", { params: showTrash ? { trash: true } : {} });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [showTrash]);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(Array.isArray(data) ? data : []);
    } catch {
      // silente: la ricerca cliente resta disponibile ma vuota
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadClients(); }, [loadClients]);

  // 🆕 Feb 2026 — Selezione cliente da combobox: auto-compila nome/email/cellulare
  // Usa `subject_type === 'company'` per riconoscere le aziende e `ragione_sociale` come nome ufficiale.
  // Ignora surname '-' (placeholder legacy per aziende).
  const applyClientToForm = (clientId) => {
    setSelectedClientId(clientId);
    if (!clientId) return;
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    const isCompany = c.subject_type === "company";
    let fullName;
    if (isCompany) {
      fullName = (c.ragione_sociale || c.company_name || c.name || "").trim();
    } else {
      const surname = (c.surname || "").trim();
      const name = (c.name || "").trim();
      const cleanSurname = surname === "-" ? "" : surname;
      fullName = `${cleanSurname} ${name}`.replace(/\s+/g, " ").trim();
    }
    const email = (isCompany ? (c.legale_rappresentante_email || c.email) : c.email) || "";
    const mobile = (isCompany ? (c.legale_rappresentante_mobile || c.mobile || c.phone) : (c.mobile || c.phone)) || "";
    setNewForm((f) => ({
      ...f,
      recipient_name: fullName || f.recipient_name,
      recipient_email: email || f.recipient_email,
      recipient_mobile: mobile || f.recipient_mobile,
    }));
  };

  const resetNewForm = () => {
    setNewForm({ title: "", recipient_name: "", recipient_email: "", recipient_mobile: "", notify_email: "", file: null });
    setSelectedClientId("");
    setRequireDrawn(false);
    setPlacements([]);
  };

  // 🆕 Feb 2026 — Selezione file con validazione: solo PDF, max 20 MB
  const pickFile = (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) {
      toast.error(`Formato non supportato: ${file.name}. Carica un file PDF.`);
      return;
    }
    const MAX_BYTES = 20 * 1024 * 1024; // 20 MB
    if (file.size > MAX_BYTES) {
      toast.error(`Il file supera i 20 MB (${(file.size / (1024 * 1024)).toFixed(1)} MB). Riduci le dimensioni o dividilo in più PDF.`);
      return;
    }
    setNewForm((f) => ({ ...f, file }));
    toast.success(`File caricato: ${file.name}`);
  };

  const createFreehand = async () => {
    const f = newForm;
    if (!f.title.trim()) return toast.error("Titolo obbligatorio");
    if (!f.recipient_email.trim()) return toast.error("Email destinatario obbligatoria");
    if (!f.file) return toast.error("Carica un file PDF");
    setSavingNew(true);
    try {
      const fd = new FormData();
      fd.append("title", f.title.trim());
      fd.append("recipient_name", f.recipient_name.trim());
      fd.append("recipient_email", f.recipient_email.trim());
      if (f.recipient_mobile.trim()) fd.append("recipient_mobile", f.recipient_mobile.trim());
      if (f.notify_email.trim()) fd.append("notify_email", f.notify_email.trim());
      if (requireDrawn) fd.append("require_drawn_signature", "true");
      if (placements.length > 0) {
        // Segnaposti già normalizzati 0..1 dal canvas visuale
        const norm = placements.map((p) => ({
          page: parseInt(p.page, 10) || 1,
          x: Math.max(0, Math.min(1, parseFloat(p.x) || 0)),
          y: Math.max(0, Math.min(1, parseFloat(p.y) || 0)),
          w: Math.max(0.02, Math.min(1, parseFloat(p.w) || 0.25)),
          h: Math.max(0.01, Math.min(1, parseFloat(p.h) || 0.06)),
        }));
        fd.append("signature_placements", JSON.stringify(norm));
      }
      fd.append("file", f.file);
      const { data } = await uploadWithProgress({
        label: `📄 Upload documento: ${f.file.name}`,
        run: (cfg) => api.post("/freehand-documents", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      toast.success(`Documento "${data.title}" creato in bozza`);
      setOpenNew(false);
      resetNewForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNew(false);
    }
  };

  // 🆕 Feb 2026 — Anteprima pre-invio richiesta firma
  // 🆕 Feb 2026 — Selezione multipla + eliminazione OTP
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  };
  const openDelete = (ids) => {
    if (!ids || ids.length === 0) { toast.error("Seleziona almeno un documento"); return; }
    setDeleteDialog({ ids, stage: "confirm" });
    setDeleteOtp("");
    setDeleteOtpSent(false);
  };
  const requestDeleteOtp = async () => {
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/admin/request-action-otp", { action: "delete_freehand_documents" });
      if (data?.ok) {
        toast.success(`Codice OTP inviato a ${data.delivered_to || "email admin"}`);
        setDeleteOtpSent(true);
        setDeleteDialog((d) => ({ ...d, stage: "otp" }));
      } else {
        toast.error("Impossibile inviare l'OTP");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  };
  const confirmDelete = async () => {
    if (!deleteDialog?.ids?.length) return;
    const otp = (deleteOtp || "").trim();
    if (!/^\d{4,10}$/.test(otp)) { toast.error("Inserisci il codice OTP ricevuto via email"); return; }
    setDeleteBusy(true);
    try {
      const endpoint = showTrash ? "/freehand-documents/purge" : "/freehand-documents";
      const { data } = await api.delete(endpoint, { data: { ids: deleteDialog.ids, otp } });
      const n = data.purged ?? data.moved_to_trash ?? 0;
      toast.success(showTrash
        ? `Eliminati definitivamente ${n} documenti`
        : `Spostati nel cestino ${n} documenti (ripristinabili entro 30 giorni)`);
      setSelectedIds(new Set());
      setDeleteDialog(null);
      setDeleteOtp("");
      setDeleteOtpSent(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  // 🆕 Feb 2026 — Ripristina documenti dal cestino
  const restoreDocs = async (ids) => {
    if (!ids?.length) return;
    try {
      const { data } = await api.post("/freehand-documents/restore", { ids });
      toast.success(`Ripristinati ${data.restored || 0} documenti`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendForSignature = async (fh) => {
    // Skip preview se toggle è attivo nella sessione
    if (isPreviewSkipped()) {
      return doSendSignature(fh);
    }
    // Apri dialog di preview con blob URL per anteprima PDF
    let pdfBlobUrl = null;
    try {
      const resp = await api.get(`/freehand-documents/${fh.id}/pdf`, { responseType: "blob" });
      pdfBlobUrl = URL.createObjectURL(resp.data);
    } catch { /* PDF preview non disponibile, ma proseguiamo senza */ }
    setPreviewDialog({
      fh,
      data: {
        title: fh.title || "Documento",
        recipient_name: fh.recipient_name,
        recipient_email: fh.recipient_email,
        recipient_mobile: fh.recipient_mobile,
        pdf_url: pdfBlobUrl,
        doc_type_label: "Firma libera",
        require_drawn_signature: !!fh.require_drawn_signature,
      },
    });
  };

  const doSendSignature = async (fh) => {
    try {
      await api.post(`/freehand-documents/${fh.id}/send-signature`);
      toast.success(`Invito firma inviato a ${fh.recipient_email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmPreviewSend = async () => {
    if (!previewDialog?.fh) return;
    setPreviewBusy(true);
    try {
      await doSendSignature(previewDialog.fh);
      setPreviewDialog(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  // 🆕 Feb 2026 — Rinvia l'email di invito (senza OTP) al cliente per una firma già in stato "sent".
  const resendInvitation = async (fh) => {
    if (!fh.signature_id) {
      toast.error("Nessuna richiesta di firma associata");
      return;
    }
    try {
      const { data } = await api.post(`/signatures/${fh.signature_id}/resend-invitation`);
      if (data?.ok) {
        toast.success(`Invito reinviato a ${fh.recipient_email}`);
      } else {
        toast.error(data?.delivery_note || "Impossibile reinviare l'invito");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const StatusBadgeInline = StatusBadge;  // alias per non riorganizzare i JSX successivi

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="signature-center-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900">
            <PenTool className="w-6 h-6 text-indigo-600" />
            Centro Firme
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Documenti PDF ad hoc da far firmare via OTP, non legati a polizze/preventivi. Per le firme documenti CRM vai su <a href="/signatures" className="text-indigo-600 hover:underline">Firme OTP</a>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { setShowTrash((v) => !v); setSelectedIds(new Set()); }} data-testid="sigcenter-toggle-trash" title={showTrash ? "Torna ai documenti attivi" : "Mostra il cestino"}>
            {showTrash ? (
              <><FileSignature className="w-4 h-4 mr-1" /> Vai agli Attivi</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-1" /> Cestino</>
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="sigcenter-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpenTitles(true)} data-testid="sigcenter-manage-titles" title="Gestisci l'elenco dei titoli documento firma">
            <Settings className="w-4 h-4 mr-1" />
            Gestisci Titoli
          </Button>
          {selectedIds.size > 0 && showTrash && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => restoreDocs(Array.from(selectedIds))}
              className="bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="sigcenter-restore-selected"
            >
              <Repeat className="w-4 h-4 mr-1" />
              Ripristina ({selectedIds.size})
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openDelete(Array.from(selectedIds))}
              className="bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100"
              data-testid="sigcenter-delete-selected"
              title={showTrash ? `Elimina definitivamente ${selectedIds.size} documenti` : `Sposta nel cestino ${selectedIds.size} documenti`}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {showTrash ? "Elimina Definitivamente" : "Sposta nel Cestino"} ({selectedIds.size})
            </Button>
          )}
          <Button size="sm" onClick={() => setOpenNew(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sigcenter-new">
            <Plus className="w-4 h-4 mr-1" />
            Nuova Firma Libera
          </Button>
        </div>
      </div>

      <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs text-slate-500 uppercase">
            <tr>
              <th className="text-center px-3 py-2 w-10">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selectedIds.size === rows.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 accent-indigo-600 cursor-pointer"
                  title="Seleziona tutto"
                  data-testid="sigcenter-select-all"
                />
              </th>
              <th className="text-left px-4 py-2">Titolo</th>
              <th className="text-left px-4 py-2">Destinatario</th>
              <th className="text-left px-4 py-2">File</th>
              <th className="text-center px-4 py-2">Stato</th>
              <th className="text-left px-4 py-2">Creato</th>
              <th className="text-right px-4 py-2">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-400">Caricamento…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400" data-testid="sigcenter-empty">
                Nessuna firma libera. Clicca <strong>Nuova Firma Libera</strong> per iniziare.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className={`border-b last:border-b-0 hover:bg-slate-50 ${selectedIds.has(r.id) ? "bg-indigo-50/40" : ""}`} data-testid={`sigcenter-row-${r.id}`}>
                <td className="text-center px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    className="h-4 w-4 accent-indigo-600 cursor-pointer"
                    data-testid={`sigcenter-select-${r.id}`}
                  />
                </td>
                <td className="px-4 py-2 font-semibold text-slate-800">{r.title}</td>
                <td className="px-4 py-2 text-slate-600">
                  <div>{r.recipient_name || <span className="italic text-slate-400">—</span>}</div>
                  <div className="text-xs text-slate-400">{r.recipient_email}</div>
                </td>
                <td className="px-4 py-2 text-xs text-slate-500 font-mono">{r.filename}</td>
                <td className="px-4 py-2 text-center"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    {r.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => sendForSignature(r)} className="bg-indigo-50 border-indigo-300 text-indigo-800 hover:bg-indigo-100" data-testid={`sigcenter-send-${r.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" />
                        Invia per Firma
                      </Button>
                    )}
                    {r.status === "sent" && r.signature_id && (
                      <Button size="sm" variant="outline" onClick={() => resendInvitation(r)} className="bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`sigcenter-resend-invite-${r.id}`} title="Rinvia email di invito al cliente">
                        <Repeat className="w-3.5 h-3.5 mr-1" />
                        Rinvia Invito
                      </Button>
                    )}
                    {r.status === "signed" && r.signature_id && (
                      <a href={`/api/signatures/${r.signature_id}/signed-pdf`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" data-testid={`sigcenter-download-${r.id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" />
                          PDF Firmato
                        </Button>
                      </a>
                    )}
                    {r.signature_id && r.status !== "draft" && (
                      <a href={`/sign/${r.signature_id}`} target="_blank" rel="noreferrer" title="Link firma pubblica">
                        <Button size="sm" variant="ghost">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDelete([r.id])}
                      className="text-rose-600 hover:bg-rose-50"
                      title="Elimina documento (richiede OTP)"
                      data-testid={`sigcenter-delete-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog nuova firma libera */}
      <Dialog open={openNew} onOpenChange={(o) => { if (!savingNew) { setOpenNew(o); if (!o) resetNewForm(); } }}>
        <DialogContent className="max-w-3xl" data-testid="sigcenter-new-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-indigo-600" />
              Nuova Firma Libera
            </DialogTitle>
            <DialogDescription>
              Carica un PDF, specifica il destinatario. Il documento resterà in bozza fino al click su <strong>Invia per Firma</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="w-full">
              <DocumentTitleSelect
                key={`sig-title-${titlesVersion}`}
                value={newForm.title}
                onChange={(v) => setNewForm({ ...newForm, title: v })}
                testid="sigcenter-title"
                label="Titolo Documento *"
              />
            </div>
            {/* 🆕 Feb 2026 — Ricerca cliente in archivio: auto-compila Nome/Email/Cellulare.
                Lasciare vuoto per inserire manualmente i dati di un nuovo destinatario. */}
            <div className="w-full space-y-1.5">
              <Label>Cliente in archivio (opzionale)</Label>
              <ClientCombobox
                clients={clients}
                value={selectedClientId}
                onChange={applyClientToForm}
                onClientCreated={(c) => { setClients((prev) => [c, ...prev.filter((x) => x.id !== c.id)]); applyClientToForm(c.id); }}
                testid="sigcenter-client"
                placeholder="Cerca per nome, cognome, CF, email…"
              />
              <p className="text-[11px] text-slate-500">Seleziona un cliente esistente per compilare automaticamente i campi sotto, oppure lasciali per un nuovo destinatario.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="w-full space-y-1.5">
                <Label htmlFor="sig-rec-name">Nome destinatario</Label>
                <Input id="sig-rec-name" value={newForm.recipient_name} onChange={(e) => setNewForm({ ...newForm, recipient_name: e.target.value })} className="w-full" data-testid="sigcenter-input-recname" />
              </div>
              <div className="w-full space-y-1.5">
                <Label htmlFor="sig-rec-mobile">Cellulare destinatario</Label>
                <Input id="sig-rec-mobile" type="tel" value={newForm.recipient_mobile} onChange={(e) => setNewForm({ ...newForm, recipient_mobile: e.target.value })} placeholder="+39 …" className="w-full" data-testid="sigcenter-input-recmobile" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="w-full space-y-1.5">
                <Label htmlFor="sig-rec-email">Email destinatario *</Label>
                <Input id="sig-rec-email" type="email" value={newForm.recipient_email} onChange={(e) => setNewForm({ ...newForm, recipient_email: e.target.value })} className="w-full" data-testid="sigcenter-input-recemail" />
              </div>
              <div className="w-full space-y-1.5">
                <Label htmlFor="sig-notify">Email dove ricevere il PDF firmato</Label>
                <StaffEmailCombobox
                  value={newForm.notify_email}
                  onChange={(v) => setNewForm({ ...newForm, notify_email: v })}
                  testid="sigcenter-notify"
                  placeholder="Seleziona uno staff o digita un'email…"
                  defaultToCurrentUser
                />
                <p className="text-[11px] text-slate-500">Se vuoto verrà usata la tua email dell&apos;account. Puoi selezionare un collaboratore oppure digitare un&apos;email diversa.</p>
              </div>
            </div>
            {/* 🆕 Feb 2026 — Segnaposti firma sul PDF (opt-in, posizionamento visuale) */}
            <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                  <MousePointerClick className="w-4 h-4 text-indigo-600" />
                  Segnaposto firma sul PDF <span className="text-xs font-normal text-slate-500">(opzionale)</span>
                </div>
                <div className="flex items-center gap-2">
                  {placements.length > 0 && (
                    <>
                      <span className="text-[11px] rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5">
                        <strong>{placements.length}</strong> segnaposto
                      </span>
                      <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50" onClick={() => setPlacements([])} data-testid="sigcenter-clear-placements">
                        <XIcon className="h-3.5 w-3.5 mr-1" /> Rimuovi tutti
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (!newForm.file) { toast.error("Carica prima il PDF, poi posiziona la firma."); return; }
                      setOpenPlacementUi(true);
                    }}
                    data-testid="sigcenter-open-placement-ui"
                    className="text-xs"
                  >
                    <MousePointerClick className="w-3.5 h-3.5 mr-1" />
                    {placements.length > 0 ? "Modifica posizionamento" : "Posiziona sul PDF"}
                  </Button>
                </div>
              </div>
              {placements.length === 0 ? (
                <p className="text-[11px] text-slate-500">
                  Clicca <strong>Posiziona sul PDF</strong> per aprire l&apos;anteprima del documento e scegliere visualmente dove apparirà la firma (con timbro di certificazione).
                </p>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="sigcenter-placements-list">
                  {placements.map((p, i) => (
                    <li key={i} className="flex items-center justify-between text-[11px] bg-white border border-slate-200 rounded px-2 py-1">
                      <span className="text-slate-700">
                        Pag. <strong>{p.page}</strong> · pos <span className="tabular-nums">{Math.round((p.x || 0) * 100)}%,{Math.round((p.y || 0) * 100)}%</span> · dim <span className="tabular-nums">{Math.round((p.w || 0) * 100)}×{Math.round((p.h || 0) * 100)}%</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setPlacements(placements.filter((_, ii) => ii !== i))}
                        className="text-rose-600 hover:bg-rose-50 rounded p-1"
                        data-testid={`sigcenter-placement-${i}-remove`}
                        title="Rimuovi"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* 🆕 Feb 2026 — Opzione: richiedi firma grafica manuale (canvas) oltre all'OTP */}
            <div className="w-full flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <input
                id="sig-require-drawn"
                type="checkbox"
                checked={requireDrawn}
                onChange={(e) => setRequireDrawn(e.target.checked)}
                className="mt-1 h-4 w-4 accent-indigo-600"
                data-testid="sigcenter-require-drawn"
              />
              <label htmlFor="sig-require-drawn" className="text-sm text-slate-700 select-none cursor-pointer">
                <span className="font-medium">Richiedi anche firma grafica manuale</span>
                <span className="block text-xs text-slate-500">Il cliente dovrà disegnare la firma con dito o mouse nella pagina di firma, oltre a inserire l&apos;OTP.</span>
              </label>
            </div>
            <div className="w-full space-y-1.5">
              <Label>File PDF *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => { pickFile(e.target.files?.[0] || null); e.target.value = ""; }}
                data-testid="sigcenter-input-file"
              />
              {!newForm.file ? (
                <DropZone
                  onFiles={(files) => pickFile(files[0])}
                  accept={["application/pdf"]}
                  multiple={false}
                  testid="sigcenter-file-drop"
                  className="mt-1 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 hover:bg-indigo-50/40 hover:border-indigo-400 cursor-pointer transition-colors"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); } }}
                    className="flex flex-col items-center justify-center py-8 px-4 text-center"
                    data-testid="sigcenter-file-browse"
                  >
                    <UploadCloud className="w-10 h-10 text-indigo-500 mb-2" />
                    <div className="text-sm font-semibold text-slate-800">Trascina qui il PDF</div>
                    <div className="text-xs text-slate-500 mt-1">
                      oppure <span className="text-indigo-600 underline">clicca per selezionarlo</span> dal computer
                    </div>
                    <div className="text-[11px] text-slate-400 mt-2">Formato accettato: <span className="font-mono">.pdf</span> · Max 20 MB</div>
                  </div>
                </DropZone>
              ) : (
                <div className="mt-1 border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4 flex items-center gap-3" data-testid="sigcenter-file-preview">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 font-semibold text-emerald-900 truncate">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      <span className="truncate" data-testid="sigcenter-file-name">{newForm.file.name}</span>
                    </div>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      {formatFileSize(newForm.file.size)} · Pronto per l&apos;invio
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-slate-700"
                      data-testid="sigcenter-file-replace"
                    >
                      Cambia
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setNewForm((f) => ({ ...f, file: null }))}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      title="Rimuovi file"
                      data-testid="sigcenter-file-remove"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenNew(false); resetNewForm(); }} disabled={savingNew} data-testid="sigcenter-new-cancel">Annulla</Button>
            <Button onClick={createFreehand} disabled={savingNew} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sigcenter-new-save">
              {savingNew ? "Caricamento…" : "Crea bozza"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog anteprima pre-invio (skip-able per sessione) */}
      <SignaturePreviewDialog
        open={!!previewDialog}
        onOpenChange={(v) => { if (!v) setPreviewDialog(null); }}
        data={previewDialog?.data}
        onConfirm={confirmPreviewSend}
        loading={previewBusy}
      />

      {/* 🆕 Feb 2026 — Dialog "Gestisci Titoli" spostato qui da AgencySettings */}
      <Dialog open={openTitles} onOpenChange={setOpenTitles}>
        <DialogContent className="max-w-lg" data-testid="sigcenter-titles-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-indigo-600" />
              Gestisci Titoli Documento Firma
            </DialogTitle>
          </DialogHeader>
          <SignatureDocTypesEditor
            onSaved={() => setTitlesVersion((v) => v + 1)}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setOpenTitles(false); setTitlesVersion((v) => v + 1); }}
              data-testid="sigcenter-titles-close"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Feb 2026 — Dialog conferma eliminazione con OTP admin */}
      <Dialog open={!!deleteDialog} onOpenChange={(v) => { if (!v && !deleteBusy) { setDeleteDialog(null); setDeleteOtp(""); setDeleteOtpSent(false); } }}>
        <DialogContent className="max-w-md" data-testid="sigcenter-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" />
              Eliminazione documenti firma
            </DialogTitle>
          </DialogHeader>
          {deleteDialog?.stage === "confirm" && (
            <div className="py-3 space-y-3">
              {showTrash ? (
                <>
                  <p className="text-sm text-slate-700">
                    Stai per eliminare <strong>DEFINITIVAMENTE</strong> <strong>{deleteDialog.ids.length}</strong> {deleteDialog.ids.length === 1 ? "documento" : "documenti"} dal cestino.
                    Non sarà più possibile recuperarli. <strong>Operazione irreversibile</strong>.
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-700">
                  Stai per spostare nel <strong>cestino</strong> <strong>{deleteDialog.ids.length}</strong> {deleteDialog.ids.length === 1 ? "documento" : "documenti"} del Centro Firme.
                  Potrai ripristinarli entro <strong>30 giorni</strong>, poi verranno rimossi automaticamente. Le firme OTP associate saranno cancellate immediatamente.
                </p>
              )}
              <p className="text-xs text-slate-500">
                Per confermare, invieremo un codice OTP alla tua email di amministratore.
              </p>
            </div>
          )}
          {deleteDialog?.stage === "otp" && (
            <div className="py-3 space-y-3">
              <p className="text-sm text-slate-700">
                Codice OTP inviato via email. Inseriscilo qui sotto per confermare l&apos;eliminazione di <strong>{deleteDialog.ids.length}</strong> {deleteDialog.ids.length === 1 ? "documento" : "documenti"}.
              </p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={deleteOtp}
                onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="Codice OTP"
                className="text-center text-xl font-mono tracking-widest"
                autoFocus
                data-testid="sigcenter-delete-otp-input"
              />
              <button
                type="button"
                onClick={requestDeleteOtp}
                disabled={deleteBusy}
                className="text-xs text-indigo-700 hover:underline disabled:opacity-50"
                data-testid="sigcenter-delete-otp-resend"
              >
                🔄 Non hai ricevuto l&apos;email? Rinvia il codice
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={deleteBusy} data-testid="sigcenter-delete-cancel">
              Annulla
            </Button>
            {deleteDialog?.stage === "confirm" && (
              <Button
                onClick={requestDeleteOtp}
                disabled={deleteBusy}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="sigcenter-delete-request-otp"
              >
                {deleteBusy ? "Invio OTP…" : "Invia OTP e prosegui"}
              </Button>
            )}
            {deleteDialog?.stage === "otp" && (
              <Button
                onClick={confirmDelete}
                disabled={deleteBusy || deleteOtp.length < 4}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="sigcenter-delete-confirm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleteBusy ? "Elimino…" : "Conferma Eliminazione"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog posizionamento visuale segnaposti firma */}
      <Dialog open={openPlacementUi} onOpenChange={setOpenPlacementUi}>
        <DialogContent className="max-w-5xl" data-testid="sigcenter-placement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-indigo-600" />
              Posiziona la firma sul PDF
            </DialogTitle>
            <DialogDescription>
              Clicca sulla pagina per aggiungere un segnaposto. Trascinalo per spostarlo, usa la maniglia in basso a destra per ridimensionarlo. Nel PDF firmato, in ogni segnaposto apparirà la firma del cliente e in fondo alla pagina un timestamp di certificazione.
            </DialogDescription>
          </DialogHeader>
          <SignaturePlacementCanvas
            file={newForm.file}
            value={placements}
            onChange={setPlacements}
            testid="sigcenter-canvas"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlacements([])} data-testid="sigcenter-canvas-reset">
              <XIcon className="h-4 w-4 mr-1" /> Svuota
            </Button>
            <Button onClick={() => setOpenPlacementUi(false)} data-testid="sigcenter-canvas-done">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Fatto ({placements.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
